import { defineComponent, type FunctionalComponent } from 'vue'
import EmptyState from 'components/empty-state'
import InfoCircle from 'assets/img/svg/icons/info-circle.svg?url'
import LinkIcon from 'assets/img/svg/icons/chain-link.svg?url'
import Button from 'components/button'
import type { UseProviderImport } from '../../hooks/useProviderImport'
import { patchModal } from 'components/modal/store'
import { sendFeaturebaseFeedback } from '@/integrations/featurebase/utils'

export const FinsquidFailed = defineComponent<{
  importAssets: UseProviderImport;
}>(
  (props) => {
    const loading = ref<boolean>(false)
    const failureStatus = computed(() => props.importAssets.failureStatus.value)
    const isUnsupportedDevice = computed(() => failureStatus.value?.status === 'unsupported_device')

    const EmptyStateAction: FunctionalComponent = () => {
      return (
        <Button
          icon={{
            src: LinkIcon,
            stroke: 'currentColor',
            size: 16
          }}
          isLoading={loading.value}
          onClick={async () => {
            loading.value = true
            await nextTick()
            await props.importAssets.reset();
            patchModal((s) => {
              s.hideTopBar = true;
              return s;
            })
            loading.value = false
          }}
        >
          {isUnsupportedDevice.value ? 'Go Back' : 'Try Again'}
        </Button>
      )
    }

    const provider = props.importAssets.provider.value?.displayName || 'the provider'

    const SupportString = () => {
      return (
        <div class="text-sm text-center text-gray-500 font-normal font-['Inter'] leading-[21px]">
          If this issue keeps appearing, please&nbsp;
          <a
            class="text-sm text-primary-500 hover:text-primary-600 font-bold font-['Inter'] underline leading-[21px] cursor-pointer"
            onClick={() => sendFeaturebaseFeedback()}
          >
            Contact Us
          </a>
          &nbsp;and we'll help you out!
        </div>
      )
    }

    return () => {
      const title = isUnsupportedDevice.value
        ? 'Provider Not Available on Mobile'
        : 'Authentication Failed!'

      const subtitle = isUnsupportedDevice.value
        ? `${provider} is only available on desktop devices. Please use a desktop computer to connect to this provider.`
        : `Authentication with ${provider} was unsuccessful. This might be due to cancelled authentication or timeout.`

      return (
        <div class="w-full h-full flex flex-col items-center justify-center px-4 md:px-0">
          <EmptyState
            class="max-w-[430px]"
            title={title}
            subtitle={subtitle}
            icon={{
              src: InfoCircle,
              size: '2rem',
              fill: '#9CA3AF',
              stroke: 'current-color'
            }}
            action={() => <EmptyStateAction />}
          />
          {!isUnsupportedDevice.value && <SupportString />}
        </div>
      )
    }
  },
  { props: ['importAssets'] }
)
